import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Navigate, useNavigate } from 'react-router-dom';
import { useUrlState } from '../hooks/useUrlState';
import Swal from 'sweetalert2';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faVideo, faCalendarDay, faPlus, faUsers, faLocationDot, faBolt, faSpinner } from '@fortawesome/free-solid-svg-icons';
import api from '../utils/api';
import { useMeetingsAccess } from '../utils/meetingsAccess';
import MeetingFormModal from '../components/meeting/MeetingFormModal';
import MeetingDrawer from '../components/meeting/MeetingDrawer';
import {
    RSVP_LABELS, belongsToTab, dayText, listWindow, rangeText
} from '../components/meeting/meetingHelpers';
import '../styles/meetings.css';

const readUser = () => {
    try { return JSON.parse(localStorage.getItem('user')) || {}; } catch { return {}; }
};

/**
 * Meetings & Events - the list. Hidden unless the server says this person is
 * in the pilot (see utils/meetingsAccess.js); everyone else is sent back to
 * the dashboard as if the page did not exist.
 */
const Meetings = () => {
    const { enabled, loaded } = useMeetingsAccess();
    const currentUser = useMemo(readUser, []);

    const [tab, setTab] = useUrlState('tab', 'upcoming'); // 'upcoming' | 'past'
    const [showCancelled, setShowCancelled] = useState(false);
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    const [form, setForm] = useState(null);   // null | { kind } | { meeting }
    const [openId, setOpenId] = useState(null);

    // "Join with a code": the abc-defg-hij from an invitation or a colleague.
    const navigate = useNavigate();
    const [code, setCode] = useState('');
    // A code, or a whole pasted link: either way, the same door as everybody's shared link.
    const joinByCode = (e) => {
        e.preventDefault();
        const found = code.trim().toLowerCase().match(/[a-z]{3}-[a-z]{4}-[a-z]{3}/);
        if (!found) {
            Swal.fire('Check the code', 'A meeting code looks like abc-defg-hij.', 'info');
            return;
        }
        navigate(`/meetings/join/${found[0]}`);
    };

    // Start a meeting right now and go to its pre-join screen. Anyone else waits in the lobby.
    const [starting, setStarting] = useState(false);
    const startInstant = async () => {
        if (starting) return;
        setStarting(true);
        try {
            const res = await api.post('/meetings/instant', {});
            navigate(`/meetings/${res.data._id}/room`);
        } catch (err) {
            Swal.fire('Could not start the meeting', err.response?.data?.message || 'Please try again.', 'error');
            setStarting(false);
        }
    };

    // `quiet` is the background refresh: no "Loading..." flash, no error pop-up.
    const load = useCallback(async ({ quiet = false } = {}) => {
        if (!quiet) setLoading(true);
        try {
            const now = new Date();
            const range = listWindow(tab, now);
            const res = await api.get('/meetings', {
                params: {
                    from: range.from.toISOString(),
                    to: range.to.toISOString(),
                    includeCancelled: showCancelled ? 'true' : undefined
                }
            });
            // The server returns everything that overlaps the window; the clock
            // decides which side of "now" each one is on.
            setItems((res.data || []).filter((m) => belongsToTab(m, tab, now)));
        } catch (err) {
            if (!quiet) {
                Swal.fire('Error', err.response?.data?.message || 'Could not load meetings.', 'error');
                setItems([]);
            }
        } finally {
            if (!quiet) setLoading(false);
        }
    }, [tab, showCancelled]);

    useEffect(() => {
        if (!enabled) return undefined;
        load();
        // A meeting moves from Upcoming to Past by itself as time passes.
        const timer = setInterval(() => load({ quiet: true }), 60000);
        return () => clearInterval(timer);
    }, [enabled, load]);

    // Newest first in the past tab, soonest first in the upcoming one.
    const days = useMemo(() => {
        const sorted = [...items].sort((a, b) => (tab === 'past'
            ? new Date(b.startsAt) - new Date(a.startsAt)
            : new Date(a.startsAt) - new Date(b.startsAt)));
        const groups = [];
        sorted.forEach(m => {
            const key = new Date(m.startsAt).toDateString();
            const last = groups[groups.length - 1];
            if (last && last.key === key) last.items.push(m);
            else groups.push({ key, date: m.startsAt, items: [m] });
        });
        return groups;
    }, [items, tab]);

    const open = items.find(m => m._id === openId) || null;

    // An answer, edit or cancellation comes back as the fresh record.
    const replace = (fresh) => setItems(list => list.map(m => (m._id === fresh._id ? fresh : m)));

    const saved = (fresh) => {
        setForm(null);
        setItems(list => (list.some(m => m._id === fresh._id) ? list.map(m => (m._id === fresh._id ? fresh : m)) : [...list, fresh]));
        setOpenId(fresh._id);
        // A new entry may fall outside this tab's window; reload to be sure.
        load();
    };

    if (!loaded) return null;
    if (!enabled) return <Navigate to="/dashboard" replace />;

    return (
        <div className="mt-page">
            <div className="mt-head">
                <div>
                    <h2>Meetings &amp; Events</h2>
                    <p className="mt-subtle">Plan meetings, see what you have been invited to, and answer invitations.</p>
                </div>
                <div className="mt-head-actions">
                    <button type="button" className="mt-btn primary" onClick={startInstant} disabled={starting}>
                        <FontAwesomeIcon icon={starting ? faSpinner : faBolt} spin={starting} /> Instant meeting
                    </button>
                    <button type="button" className="mt-btn ghost" onClick={() => setForm({ kind: 'meeting' })}>
                        <FontAwesomeIcon icon={faVideo} /> Schedule meeting
                    </button>
                    <button type="button" className="mt-btn ghost" onClick={() => setForm({ kind: 'event' })}>
                        <FontAwesomeIcon icon={faPlus} /> New event
                    </button>
                </div>
            </div>

            <form className="mt-code-form" onSubmit={joinByCode}>
                <input
                    type="text"
                    value={code}
                    maxLength={200}
                    placeholder="Join with a code or link, e.g. abc-defg-hij"
                    onChange={(e) => setCode(e.target.value)}
                />
                <button type="submit" className="mt-btn ghost" disabled={!code.trim()}>Join</button>
            </form>

            <div className="mt-toolbar">
                <div className="mt-tabs" role="tablist">
                    <button type="button" className={tab === 'upcoming' ? 'on' : ''} onClick={() => setTab('upcoming')}>Upcoming</button>
                    <button type="button" className={tab === 'past' ? 'on' : ''} onClick={() => setTab('past')}>Past</button>
                </div>
                <label className="mt-check">
                    <input type="checkbox" checked={showCancelled} onChange={(e) => setShowCancelled(e.target.checked)} />
                    Show cancelled
                </label>
            </div>

            {loading ? (
                <p className="mt-empty">Loading...</p>
            ) : days.length === 0 ? (
                <div className="mt-empty">
                    <FontAwesomeIcon icon={faCalendarDay} />
                    <p>{tab === 'upcoming' ? 'Nothing coming up.' : 'Nothing in the last six months.'}</p>
                </div>
            ) : (
                days.map(day => (
                    <section key={day.key} className="mt-day">
                        <h4>{dayText(day.date, { weekday: 'long', day: 'numeric', month: 'long' })}</h4>
                        {day.items.map(m => {
                            const cancelled = m.status === 'cancelled';
                            const needsReply = !cancelled && m.myRsvp === 'pending';
                            return (
                                <button
                                    key={m._id}
                                    type="button"
                                    className={`mt-card ${cancelled ? 'is-cancelled' : ''} ${needsReply ? 'needs-reply' : ''}`}
                                    onClick={() => setOpenId(m._id)}
                                >
                                    <span className="mt-card-time">{rangeText(m)}</span>
                                    <span className="mt-card-main">
                                        <span className="mt-card-title">
                                            <FontAwesomeIcon icon={m.kind === 'meeting' ? faVideo : faCalendarDay} />
                                            {m.title}
                                        </span>
                                        <span className="mt-card-meta">
                                            {m.organizer?.name && <span>By {m.myRole === 'organizer' ? 'you' : m.organizer.name}</span>}
                                            <span><FontAwesomeIcon icon={faUsers} /> {m.participants.length}</span>
                                            {m.location && <span><FontAwesomeIcon icon={faLocationDot} /> {m.location}</span>}
                                        </span>
                                    </span>
                                    {cancelled
                                        ? <span className="mt-rsvp-tag declined">Cancelled</span>
                                        : m.myRsvp && m.myRole !== 'organizer' && (
                                            <span className={`mt-rsvp-tag ${m.myRsvp}`}>{RSVP_LABELS[m.myRsvp]}</span>
                                        )}
                                </button>
                            );
                        })}
                    </section>
                ))
            )}

            {open && !form && (
                <MeetingDrawer
                    meeting={open}
                    onClose={() => setOpenId(null)}
                    onChanged={replace}
                    onEdit={(m) => setForm({ meeting: m })}
                />
            )}

            {form && (
                <MeetingFormModal
                    meeting={form.meeting}
                    kind={form.kind}
                    currentUser={currentUser}
                    onClose={() => setForm(null)}
                    onSaved={saved}
                />
            )}
        </div>
    );
};

export default Meetings;
